// BCM-898 · Goods Receipt Dashboard — action feedback layer.
//
// One place that every action reports success/failure to, so the dashboard
// behaves consistently (the pieces the 25-Aug review asked for):
//   * Every update shows the 4s green/red toast for the message — unchanged.
//   * Errors show "Something went wrong. Please try again." (Batch Execution copy).
//   * A status-changing update polls the LIGHTWEIGHT A22 status endpoint
//     (fetchBatchStatusDirect — one-row read of statusID + updatedAt)
//     every 2s until the batch settles (see pollUntilStatusChanges), then runs
//     ONE full refetch — because the Temporal workflow advances the batch
//     asynchronously, so an immediate refetch would otherwise show the previous
//     pending action (the exact issue the Slack video showed after Mark Review
//     as Complete).
//   * While that poll runs, a NON-BLOCKING "Updating Batch" toast (bottom-right,
//     hourglass icon, elapsed-time progress bar — UpdatingBatchToast.jsx) tells
//     the user the batch is still updating. Replaces the previous app-wide
//     blocking backdrop for GR: the user can keep scrolling the table and open
//     other rows while the workflow settles.
//   * The poll starts right away, alongside the success toast. It used to wait
//     for the toast to close (TOAST_MS, 10s), which was pure dead time — the
//     poll only refetches once the batch has settled, so it can't surface stale
//     data the way an immediate refetch did. A non-status update still waits for
//     the toast to close before its single refetch (unless `refetchNow`); the
//     error toast never triggers a refetch.
//
// Usage: const fb = useGRFeedback();
//   fb.notifySuccess({ message, statusChanging, batchId, prevStatus, refetch, actionLabel, resetsBatch, mayKeepStatus, refetchNow });
//   fb.notifyError(message?);   // message defaults to the generic copy
// `refetch` is the caller's onRefresh; `actionLabel` (optional) is shown in the
// UpdatingBatchToast title, e.g. "Edit Date of Manufacture triggered".
// `resetsBatch` (optional) — set it when the action RESTARTS the workflow
// (Update & Re-run, Sync PO / Inventory, QA sync) rather than advancing it one
// step. The poll then waits for the status to hold steady instead of settling on
// the first change; see pollUntilStatusChanges.
// `mayKeepStatus` (optional) — set it for a step that may leave the status
// unchanged (Mark Review as Complete). If the status hasn't changed after
// MAY_KEEP_STATUS_READS reads, the poll settles there; if it has, the poll waits
// for it to hold steady exactly like a reset.
// `refetchNow` (optional, non-status updates only) — refetch right away instead
// of after the toast closes, for updates with no workflow to settle (a field save).
import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from "react";
import { Snackbar, Paper, Typography, IconButton, Box } from "@material-ui/core";
import CheckCircleOutlineRoundedIcon from "@material-ui/icons/CheckCircleOutlineRounded";
import ErrorIcon from "@material-ui/icons/ErrorOutline";
import CloseIcon from "@material-ui/icons/Close";
import { TOAST_MS, GENERIC_ERROR, STATUS } from "../utils/constants";
import { fetchBatchWorkflowStatus } from "../api/goodsReceiptApi";
import UpdatingBatchToast from "../components/UpdatingBatchToast";
import { GR_FONT_FAMILY } from "../goodsReceiptStyles";

const GRFeedbackContext = createContext(null);

export const useGRFeedback = () => {
  const ctx = useContext(GRFeedbackContext);
  // A no-op fallback keeps action components safe if one is ever rendered outside
  // the provider (e.g. in isolation / a test) — they simply get no toast.
  return (
    ctx || {
      notifySuccess: () => {},
      notifyError: () => {},
      busy: false,
    }
  );
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// Status-change poll schedule — see pollUntilStatusChanges.
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS = 45000;
// For an action that may legitimately leave the status unchanged
// (`mayKeepStatus` — Mark Review as Complete): this many status reads (~6s) with
// no change means the batch stayed put, which is a valid result — settle there.
const MAY_KEEP_STATUS_READS = 4;
// A reset (resetsBatch) restarts the workflow, which then walks the row through
// several statuses. Its status must read the same this many times in a row (4
// reads = ~6s of stillness at POLL_INTERVAL_MS) before it counts as settled —
// long enough to ride out a pause at a half-way status such as 55.
const RESET_STABLE_READS = 4;
// Statuses a workflow reset passes through on its way from POST_MANUFACTURING
// (3 → 5 → 55 → …). 5 is also where the re-run legitimately stops when fields
// still need review (PA2), so these can't be excluded outright — they need the
// same quiet window (4 reads = ~6s) before they count as final.
const TRANSIT_STATUSES = [STATUS.PRODUCTION_COMPLETE, STATUS.CMO_QC_RELEASED];
const TRANSIT_STABLE_READS = 4;

const GRFeedbackProvider = ({ children }) => {
  const [toast, setToast] = useState({ open: false, kind: "success", message: "" });
  const [busy, setBusy] = useState(false); // a status-change poll is running
  // updating = { open, actionLabel, startedAt } for the non-blocking bottom-right
  // "Updating Batch" toast that mirrors `busy`. Kept separate from `busy` so the
  // startedAt anchor survives even when the caller doesn't pass an actionLabel.
  // outcome: "polling" while the poll runs; "timeout" / "error" keep the toast
  // open with a Refresh-now button; "settled" is followed immediately by the
  // toast closing.
  const [updating, setUpdating] = useState({
    open: false,
    actionLabel: null,
    startedAt: 0,
    outcome: "polling",
    refetch: null,
  });
  const [updatingElapsed, setUpdatingElapsed] = useState(0);
  const closeUpdatingToast = useCallback(() => {
    setUpdating((u) => ({ ...u, open: false, refetch: null }));
  }, []);
  const refreshFromToast = useCallback(async () => {
    const r = updating.refetch;
    setUpdating((u) => ({ ...u, open: false, refetch: null }));
    if (r) {
      try { await r(); } catch (e) { /* non-fatal */ }
    }
  }, [updating.refetch]);
  const closeTimer = useRef(null);
  const toastClosedResolveRef = useRef(null); // resolves once the open toast actually closes
  const mounted = useRef(true);

  // Resolves any caller awaiting the toast's close (natural timeout or the user
  // dismissing it early via the X) exactly once, then clears the ref.
  const settleToastClose = useCallback(() => {
    const resolve = toastClosedResolveRef.current;
    toastClosedResolveRef.current = null;
    if (resolve) resolve();
  }, []);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (closeTimer.current) clearTimeout(closeTimer.current);
      settleToastClose(); // don't strand a caller awaiting a toast that never closes
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick the elapsed counter on the Updating Batch toast while the poll runs.
  // Kept at 500ms so the elapsed line looks live without pointless re-renders.
  useEffect(() => {
    if (!updating.open) {
      setUpdatingElapsed(0);
      return undefined;
    }
    setUpdatingElapsed(Date.now() - updating.startedAt);
    const iv = setInterval(() => {
      if (!mounted.current) return;
      setUpdatingElapsed(Date.now() - updating.startedAt);
    }, 500);
    return () => clearInterval(iv);
  }, [updating.open, updating.startedAt]);

  // Returns a promise that resolves once this toast has closed — callers use it
  // to defer any dashboard refetch until the error/success toast is actually gone.
  const openToast = useCallback(
    (kind, message) =>
      new Promise((resolve) => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        settleToastClose(); // don't strand a previous caller's wait
        toastClosedResolveRef.current = resolve;
        setToast({ open: true, kind, message });
        closeTimer.current = setTimeout(() => {
          if (mounted.current) setToast((t) => ({ ...t, open: false }));
          settleToastClose();
        }, TOAST_MS);
      }),
    [settleToastClose]
  );

  const closeToast = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setToast((t) => ({ ...t, open: false }));
    settleToastClose();
  }, [settleToastClose]);

  // Poll the LIGHTWEIGHT status endpoint (A22 fetchBatchStatusDirect —
  // one-row read of statusID + lastUpdatedDate) every POLL_INTERVAL_MS until the
  // batch settles, then run ONE table refetch. Flat 2s rather than the old
  // 2→3→5→8s backoff, which added up to 8s after the status had already landed;
  // a single-row read is cheap enough to poll at this rate up to the 45s cap.
  //
  // What "settled" means depends on the action:
  //   * Single-step actions (approve variance, confirm consumption, generate
  //     IBD/GRN, mark review complete…) — the batch moves from one status to the
  //     next and rests there, so the poll settles on the FIRST read that differs
  //     from prevStatus.
  //   * Resets (`resetsBatch`: Update & Re-run, Sync PO / Inventory, QA sync)
  //     restart the workflow from POST_MANUFACTURING and walk the row through
  //     3 → 5 → 55 → 37/38 → its final status, one Corda write per step. The
  //     first changed read is then often a half-way status, and when the re-run
  //     lands back on prevStatus (e.g. 40 again) a "differs" check never fires
  //     and the poll ran to its 45s cap. A reset is settled once:
  //       - the status has differed from prevStatus at least once (the workflow
  //         has really moved — landing back on prevStatus afterwards is fine), and
  //       - it has read the same number of times in a row: RESET_STABLE_READS,
  //         or TRANSIT_STABLE_READS for a status the walk passes
  //         through (3 / 5). That is how a PA2 re-run whose fields still need
  //         review settles — 5 → 3 → 5, resting on 5.
  //     Known gaps, which run to the cap and refetch: the re-run never reads
  //     differently (a QA sync whose SAP decision is unchanged, or a short-lived
  //     status 3 that falls between two reads).
  //   * `mayKeepStatus` (Mark Review as Complete) — the batch only advances in
  //     some cases and stays on prevStatus in others:
  //       - no change in the first MAY_KEEP_STATUS_READS reads → settled (the
  //         batch stayed put);
  //       - a change within them → handled exactly like a reset: settled once
  //         the status has read the same RESET_STABLE_READS times in a row.
  //
  // Returns the outcome so the caller can decide whether to auto-close the
  // Updating Batch toast (settled) or keep it open with a Refresh-now button
  // (timeout / error).
  const pollUntilStatusChanges = useCallback(async ({ batchId, prevStatus, refetch, resetsBatch, mayKeepStatus }) => {
    if (batchId == null) {
      // Fall back to a single refetch when we have no id to poll on.
      try { await refetch(); } catch (e) { /* non-fatal */ }
      return "settled";
    }
    const started = Date.now();
    // Mark review waits for a steady status once it has moved, like a reset.
    const waitsForStable = resetsBatch || mayKeepStatus;
    // batchStatus (statusID from the endpoint) is the same column as
    // row.SASLBatchStatusID (see fetchBatchesDirect.js line 901); compared as
    // numbers so a string/number mismatch can't read as a change.
    const fromStatus = Number(prevStatus);
    let moved = false; // status has differed from prevStatus at least once
    let reads = 0; // successful status reads
    let lastStatus = null;
    let sameReads = 0;
    let transientErrors = 0;
    let outcome = "timeout"; // "settled" once stable, "error" on repeated failures
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - started >= MAX_POLL_MS) { outcome = "timeout"; break; }
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetchBatchWorkflowStatus(batchId);
        const d = (res && res.data && res.data.data) || null;
        if (d && d.batchStatus != null) {
          const status = Number(d.batchStatus);
          reads += 1;
          if (status !== fromStatus) moved = true;
          sameReads = status === lastStatus ? sameReads + 1 : 1;
          lastStatus = status;
          const stableReadsNeeded = TRANSIT_STATUSES.includes(status)
            ? TRANSIT_STABLE_READS
            : RESET_STABLE_READS;
          const settled = waitsForStable ? moved && sameReads >= stableReadsNeeded : moved;
          // Mark review that left the status unchanged: nothing more to wait for.
          const stayedPut = mayKeepStatus && !moved && reads >= MAY_KEEP_STATUS_READS;
          if (settled || stayedPut) {
            outcome = "settled";
            break;
          }
        }
      } catch (err) {
        transientErrors += 1;
        if (transientErrors > 3) { outcome = "error"; break; }
      }
      // eslint-disable-next-line no-await-in-loop
      await delay(POLL_INTERVAL_MS);
    }
    // Whatever happened above (status flipped / timeout / errors), run the
    // full refetch exactly once so the table + detail reflect current state.
    try { await refetch(); } catch (e) { /* non-fatal */ }
    return outcome;
  }, []);

  const notifySuccess = useCallback(
    async ({ message, statusChanging, batchId, prevStatus, refetch, actionLabel, resetsBatch, mayKeepStatus, refetchNow } = {}) => {
      const toastClosed = openToast("success", message || "Updated successfully.");
      if (statusChanging && refetch && batchId != null && prevStatus != null) {
        // Start polling now, alongside the success toast. One tick first so the
        // caller's own post-success state updates (e.g. setApproving(false)) run
        // before `busy` swaps the table — and the caller — for the loading row;
        // React 17 doesn't batch setState after an await.
        await delay(0);
        if (!mounted.current) return;
        setBusy(true);
        setUpdating({
          open: true,
          actionLabel: actionLabel || null,
          startedAt: Date.now(),
          outcome: "polling",
          refetch,
        });
        let outcome = "settled";
        try {
          outcome = await pollUntilStatusChanges({ batchId, prevStatus, refetch, resetsBatch, mayKeepStatus });
        } finally {
          if (mounted.current) {
            setBusy(false);
            if (outcome === "settled") {
              // Table just refetched; nothing more to say — close the toast.
              setUpdating({ open: false, actionLabel: null, startedAt: 0, outcome: "polling", refetch: null });
            } else {
              // Keep the toast open with the outcome so the user sees the
              // Refresh-now / Dismiss buttons. `refetch` is retained so
              // Refresh-now works after dismiss/close.
              setUpdating((u) => ({ ...u, outcome, refetch }));
            }
          }
        }
        return;
      }
      // Non-status update (e.g. a field save or a sync without a reset): wait for
      // the success toast to close (natural timeout
      // or an early manual dismiss), then a single refetch of the table + detail.
      // `refetchNow` skips the wait — one tick only, for the same reason as above.
      if (refetchNow) await delay(0);
      else await toastClosed;
      if (!mounted.current || !refetch) return;
      try {
        await refetch();
      } catch (e) {
        /* the toast already conveyed success; a refetch hiccup is non-fatal */
      }
    },
    [openToast, pollUntilStatusChanges]
  );

  const notifyError = useCallback(
    (message) => {
      openToast("error", message || GENERIC_ERROR);
    },
    [openToast]
  );

  const isError = toast.kind === "error";

  return (
    <GRFeedbackContext.Provider value={{ notifySuccess, notifyError, busy }}>
      {children}
      {/* Non-blocking bottom-right toast during the status-change poll. Replaces
          the old app-wide blocking BackDrop for GR-dashboard actions — the user
          can still browse the table while the workflow settles. */}
      <UpdatingBatchToast
        open={updating.open}
        outcome={updating.outcome}
        elapsedMs={updatingElapsed}
        actionLabel={updating.actionLabel}
        onRefreshNow={refreshFromToast}
        onDismiss={closeUpdatingToast}
      />
      {/* Solid colour banner, top-right — matches the confirmation toast used
          elsewhere in the app (e.g. the MARD-sync confirmation), rather than a
          white card with just a coloured icon. */}
      <Snackbar
        open={toast.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ zIndex: 2001, top: 100 }}
      >
        <Paper
          elevation={6}
          style={{
            padding: "14px 16px",
            borderRadius: 8,
            minWidth: 320,
            maxWidth: "none",
            width: "max-content",
            backgroundColor: isError ? "#C0392B" : "#2e7d32",
            fontFamily: GR_FONT_FAMILY,
          }}
        >
          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            {isError ? (
              <ErrorIcon style={{ color: "#FFFFFF" }} />
            ) : (
              <CheckCircleOutlineRoundedIcon style={{ color: "#FFFFFF" }} />
            )}
            <Typography style={{ fontWeight: 500, color: "#FFFFFF", flex: 1, whiteSpace: "nowrap" }}>
              {toast.message}
            </Typography>
            <IconButton
              size="small"
              onClick={closeToast}
              aria-label="close"
              style={{ color: "#FFFFFF", padding: 4, marginTop: -4, marginRight: -4 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Snackbar>
    </GRFeedbackContext.Provider>
  );
};

export default GRFeedbackProvider;
